import type { ReactNode } from "react";
import { ArrowLeft, X } from "lucide-react";


export function AppScreen({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <main className={`app-screen px-5 ${className}`}><div className="mx-auto w-full max-w-lg">{children}</div></main>;
}

export function AppStack({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`space-y-4 ${className}`}>{children}</div>;
}

export function AppPageHeader({ title, subtitle, onBack, action }: { title: string; subtitle?: string; onBack?: () => void; action?: ReactNode }) {
  return <header className="flex min-h-12 items-center gap-3">
    {onBack && <button type="button" onClick={onBack} aria-label="Voltar" className="glass-icon grid h-10 w-10 shrink-0 place-items-center rounded-full"><ArrowLeft className="h-4 w-4"/></button>}
    <div className="min-w-0 flex-1"><h1 className="truncate font-display text-xl font-semibold text-white">{title}</h1>{subtitle && <p className="mt-0.5 truncate text-xs text-white/45">{subtitle}</p>}</div>
    {action}
  </header>;
}

export function AppPanel({ children, className = "", tone = "default" }: { children: ReactNode; className?: string; tone?: "default" | "teal" | "amber" | "violet" }) {
  const toneClass = tone === "teal"
    ? "border-teal/15 bg-teal/[.04]"
    : tone === "amber"
      ? "border-amber-200/15 bg-amber-200/[.04]"
      : tone === "violet"
        ? "border-violet-200/12 bg-violet-200/[.03]"
        : "border-white/10 bg-white/[.035]";
  return <section className={`rounded-[1.35rem] border ${toneClass} ${className}`}>{children}</section>;
}

export function AppSectionHeader({ kicker, title, detail, action }: { kicker?: string; title: string; detail?: string; action?: ReactNode }) {
  return <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      {kicker && <p className="text-[10px] font-black uppercase tracking-[.14em] text-teal-light">{kicker}</p>}
      <h2 className="mt-1 font-display text-lg font-semibold text-white">{title}</h2>
      {detail && <p className="mt-1 text-xs leading-5 text-white/45">{detail}</p>}
    </div>
    {action}
  </div>;
}

export function AppMetric({ value, label, className = "" }: { value: ReactNode; label: string; className?: string }) {
  return <div className={`rounded-xl border border-white/8 bg-black/10 p-3 text-center ${className}`}>
    <strong className="block font-display text-lg text-white">{value}</strong>
    <span className="mt-1 block text-[9px] font-bold uppercase tracking-wide text-white/35">{label}</span>
  </div>;
}

export function AppStatusPill({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "success" | "warning" | "danger" | "info" }) {
  const cls = tone === "success" ? "bg-teal/12 text-teal-light"
    : tone === "warning" ? "bg-amber-300/10 text-amber-100"
      : tone === "danger" ? "bg-red-400/10 text-red-200"
        : tone === "info" ? "bg-violet-200/[.08] text-violet-100"
          : "bg-white/[.06] text-white/55";
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold ${cls}`}>{children}</span>;
}

export function AppEmptyState({ title, detail, className = "" }: { title: string; detail?: string; className?: string }) {
  return <div className={`rounded-xl border border-dashed border-white/10 bg-white/[.015] p-4 text-center ${className}`}>
    <strong className="text-sm text-white/60">{title}</strong>
    {detail && <p className="mt-1 text-xs leading-5 text-white/35">{detail}</p>}
  </div>;
}

export function AppSheet({ open, title, kicker, onClose, children }: { open: boolean; title: string; kicker?: string; onClose: () => void; children: ReactNode }) {
  if (!open) return null;
  return <div className="app-modal-safe fixed inset-0 z-[90] flex items-end bg-black/70 px-3 backdrop-blur-sm">
    <section role="dialog" aria-modal="true" aria-label={title} className="app-sheet-safe mx-auto w-full max-w-lg overflow-y-auto rounded-[1.8rem] border border-white/10 bg-[#071012] p-5 shadow-2xl">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">{kicker && <p className="text-[10px] font-black uppercase tracking-[.14em] text-teal-light">{kicker}</p>}<h2 className="mt-1 truncate font-display text-xl font-semibold text-white">{title}</h2></div>
        <button type="button" onClick={onClose} className="glass-icon grid h-10 w-10 shrink-0 place-items-center rounded-full" aria-label="Fechar"><X className="h-4 w-4"/></button>
      </div>
      <div className="mt-4">{children}</div>
    </section>
  </div>;
}
